import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Building2, Wifi, WifiOff } from 'lucide-react';
import type { ImpiantoSintesi } from '@emacomfort/domain';
import { useDevice, useStato } from '@/app/DeviceProvider';
import { useMarchio } from '@/app/BrandProvider';
import { useFormato } from '@/app/formato';
import { Badge, Bottone, Card, Pagina, cx, stili } from '@/ui';
import { useSondaggio } from '../comune';
import s from '../features.module.css';

export function ParcoPage() {
  const { t } = useTranslation();
  const f = useFormato();
  const navigate = useNavigate();
  const { marchio } = useMarchio();
  const { parco, impiantoId, selezionaImpianto } = useDevice();
  const adesso = useStato((st) => st.adesso);
  const carica = useCallback(() => parco.listaImpianti(), [parco]);
  const { dati } = useSondaggio<ImpiantoSintesi[]>(carica, 3000);
  const lista = dati ?? [];
  const online = lista.filter((i) => i.connessione === 'connesso').length;
  const offline = lista.filter((i) => i.connessione === 'irraggiungibile').length;
  const degradati = lista.filter((i) => i.connessione === 'degradato').length;
  const allarmi = lista.reduce((n, i) => n + i.allarmiAperti, 0);

  return (
    <Pagina titolo={t('tecnico.parco.titolo')} sottotitolo={t('tecnico.parco.sottotitolo', { installatore: marchio.nome })}>
      <div className={s.kpi}>
        {[
          [t('tecnico.parco.impianti'), lista.length],
          [t('tecnico.parco.online'), online],
          [t('tecnico.parco.degradati'), degradati],
          [t('tecnico.parco.offline'), offline],
          [t('tecnico.parco.allarmi'), allarmi],
        ].map(([etichetta, n]) => (
          <div key={String(etichetta)} className={s.kpiVoce}>
            <span className={stili.etichetta}>{etichetta}</span>
            <span className={stili.valoreSecondario}>{n}</span>
          </div>
        ))}
      </div>
      <div className={s.impianti}>
        {lista.map((i) => {
          const inUso = i.id === impiantoId;
          const tono = i.connessione === 'connesso' ? 'ok' : i.connessione === 'degradato' ? 'warn' : 'err';
          return (
            <Card key={i.id} className={cx(inUso && s.impiantoInUso)} titolo={i.nome} icona={<Building2 size={20} aria-hidden />} azione={inUso && <Badge tono="accento">{t('tecnico.parco.inUso')}</Badge>}>
              <div className={s.letturaNota}>
                {i.cliente} · {i.citta}
              </div>
              <div className={stili.riga}>
                <Badge tono={tono} icona={i.connessione === 'irraggiungibile' ? <WifiOff size={16} aria-hidden /> : <Wifi size={16} aria-hidden />}>
                  {i.connessione === 'connesso' ? t('stato.connesso') : i.connessione === 'degradato' ? t('stato.degradato') : t('stato.irraggiungibile')}
                </Badge>
                {i.allarmiAperti > 0 && (
                  <Badge tono={i.allarmeMassimo === 'critica' || i.allarmeMassimo === 'alta' ? 'err' : 'warn'}>
                    {t('tecnico.parco.allarmi')}: {i.allarmiAperti}
                  </Badge>
                )}
              </div>
              <div className={s.letturaNota}>
                {t(`tecnico.parco.centralina.${i.tipoCentralina}`)} · {t('tecnico.parco.firmware', { v: i.firmware })}
              </div>
              <div className={s.letturaNota}>
                {t('tecnico.parco.zone', { n: i.zone })} · {t('tecnico.parco.generatori', { n: i.generatori })} · {t('tecnico.parco.ultimoContatto')}: {i.ultimoContatto ? f.relativo(i.ultimoContatto, adesso) : t('tecnico.parco.mai')}
              </div>
              <Bottone
                variante={inUso ? 'base' : 'primario'}
                onClick={() => {
                  selezionaImpianto(i.id);
                  navigate('/tecnico/canali');
                }}
              >
                {t('tecnico.parco.apri')}
              </Bottone>
            </Card>
          );
        })}
      </div>
    </Pagina>
  );
}
